import React from 'react';
import { useParams } from "react-router";
import { useIntl } from "react-intl";
import classNames from "classnames";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { UTCToLocalString } from "@/util/dateUtil";
import bulkTestRepository from "@/repository/bulkTestRepository";
import { useBulkTestReport } from "@/query/bulkTest";
import { useAgentId, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import { BackIcon, DownloadIcon } from "@/assets/svgs/icons";

import SentenceResultTable from "./component/SentenceResultTable";


const BulkTestReportView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const { testId } = useParams() as {testId: string};
    const { moveToBulkTestList } = useRouteHandler();

    const { data: test } = useBulkTestReport(agentId, testId!);

    if (!test) {
        return null;
    }
    return (
        <div className="agent-content-layout overflow-y-auto">
            <div className="w-md-page w-full grid grid-cols-[minmax(0,1fr)_344px] bg-white border border-black-200 rounded-[6px] whitespace-nowrap">
                <div className="pt-6 pr-7 pb-5 pl-6 flex flex-col gap-5 border-r border-black-200">
                    <div className="flex flex-col gap-1 pb-3.5 border-b border-black-200">
                        <div className="text-black-500 text-[15px] font-[600] truncate">
                            {intl.formatMessage({ id: "i09018" })} {test.title}
                        </div>
                        <div className="text-black-400 text-[12px] font-[400]">
                            {intl.formatMessage({ id: "i09020" })}
                            {intl.formatMessage({ id: "b0019" }, { version: test.sandbox_version ?? 0 })}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-[14px] font-[400]">
                        <TestDetail label={intl.formatMessage({ id: "i09040" })}
                                    value={test.file_name}
                                    extra={
                                        <Button onClick={() => bulkTestRepository.exportCase(agentId, testId)}>
                                            <DownloadIcon className="size-3.5" />
                                        </Button>
                                    }
                        />

                        <TestDetail label={intl.formatMessage({ id: "i09041" })}
                                    value={`${test.tester_name ?? "-"} (${test.tester_email ?? "-"})`}
                        />

                        <TestDetail label={intl.formatMessage({ id: "i09042" })}
                                    value={UTCToLocalString(test.created_at, "YYYY-MM-DD")}
                        />

                        <TestDetail label={intl.formatMessage({ id: "i09043" })}
                                    value={intl.formatMessage({ id: "i09043_1" }, { score: test.threshold })}
                        />
                    </div>
                </div>

                <div className="h-full py-3 pl-4 pr-7 flex items-center gap-5">
                    <div className="relative">
                        <div className="absolute w-full text-center top-[45px]">
                            <div className="block text-black-500 text-[16px] font-[600] leading-none">
                                {test.current_success_cnt + test.current_fail_cnt}
                            </div>
                            <div className="block text-black-500 text-[13px] font-[400] leading-none">
                                {intl.formatMessage({ id: "i09025" })}
                            </div>
                        </div>
                        <Chart type="donut"
                                width={120}
                                height={120}
                                series={[test.current_success_cnt, test.current_fail_cnt]}
                                options={CHART_OPTIONS}
                        />
                    </div>

                    <div className="size-full py-7 flex flex-col gap-3 justify-between">
                        <ResultItem variant="success"
                                    label={intl.formatMessage({ id: "i09023" })}
                                    count={test.current_success_cnt}
                                    percent={test.success_percent}
                        />

                        <hr className="w-full border-t-black-200"/>

                        <ResultItem variant="fail"
                                    label={intl.formatMessage({ id: "i09024" })}
                                    count={test.current_fail_cnt}
                                    percent={test.fail_percent}
                        />
                    </div>
                </div>
            </div>

            <div className="table-btn-container w-md-page mt-4">
                <div className="ml-auto flex">
                    <Button variant="text"
                            onClick={() => moveToBulkTestList(agentId)}
                    >
                        <BackIcon className="size-3.5 mb-0.5"/>
                        {intl.formatMessage({ id: "i09044" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => bulkTestRepository.exportResult(agentId, testId)}
                    >
                        <DownloadIcon className="size-4" />
                        {intl.formatMessage({ id: "b0005" })}
                    </Button>
                </div>
            </div>

            <div className="w-md-page mt-2">
                <SentenceResultTable successes={test.success_sentences}
                                     fails={test.fail_sentences}
                />
            </div>
        </div>
    );
};

interface TestDetailProps {
    label: string;
    value?: string;
    extra?: React.ReactNode;
}

const TestDetail: React.FC<TestDetailProps> = ({ label, value, extra }) => (
    <div className="flex items-center gap-1">
        <div className="text-black-400">
            {label}
        </div>
        <div className="text-black-500">
            {value}
        </div>
        {extra}
    </div>
);

interface ResultItemProps {
    variant: 'success' | 'fail';
    label: string;
    count: number;
    percent: number;
}

const ResultItem: React.FC<ResultItemProps> = ({ variant, label, count, percent }) => (
    <div className="flex gap-7">
        <div className="flex gap-3">
            <div className={classNames(
                     "size-5 rounded-full text-[12px] text-white leading-5 text-center",
                     variant === "success" ? "bg-[#6A4ED7]" : "bg-[#FF71B5]"
                 )}
            >
                {variant === "success" ? "S" : "F"}
            </div>
            <div className="text-black-500 text-[14px] font-[400]">
                {label}
            </div>
        </div>
        <div className="text-black-500 text-[13px] font-[500]">
            {`${count} (${percent}%)`}
        </div>
    </div>
);

const CHART_OPTIONS: ApexOptions = {
    chart: {
        type: "pie",
        events: {
            click: undefined
        }
    },
    legend: {
        show: false
    },
    dataLabels: {
        enabled: false,
    },
    labels: ['a', 'b'],
    plotOptions: {
        pie: {
            startAngle: -180,
            endAngle: 180,
            expandOnClick: false,
            donut: {
                size: "85%",
            }
        }
    },
    tooltip: {
        enabled: false
    },
    states: {
        active: {
            filter: {
                type: 'none',
            }
        },
        hover: {
            filter: {
                type: 'none',
            }
        },
    },
    fill: {
        colors: ['#6A4ED7', '#FF71B5'],
        type: "solid"
    },
    stroke: {
        width: 0
    }
};

export default BulkTestReportView;
